import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle, Clock, Loader2 } from 'lucide-react'
import { eformService } from './eform.service'
import type { EFormRenderResponse } from './eform.types'
import EFormRenderer from './EFormRenderer'
import { createVisitQueue, type EFormVisitQueue } from './eformQueue'
import { toSubmitAnswers } from './eformAnswers'
import { createAutosave, type EFormAutosave } from './eformAutosave'
import { loadRecaptcha, recaptchaToken } from './recaptcha'

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; data: EFormRenderResponse }
  | { status: 'success'; message: string; pdfUrl?: string }
  | { status: 'error'; message: string; code?: string }

/** A lead (website visitor) form: no patient on the link. Only these run the bot check (R23). */
function isLeadForm(data: EFormRenderResponse): boolean {
  const patientId = Number(data.context.patientId)
  return !(Number.isFinite(patientId) && patientId > 0)
}

function loadForwardSdk(): void {
  if (document.getElementById('forward-sdk-script')) return
  if ((window as any).Forward?.createPaymentElement) return
  const script = document.createElement('script')
  script.id = 'forward-sdk-script'
  script.src = window.location.hostname.includes('staging')
    ? 'https://sandbox-cdn.pci.getfwd.com/sdk/forward.js'
    : 'https://cdn.pci.getfwd.com/sdk/forward.js'
  script.async = true
  document.head.appendChild(script)
}

function loadGoogleMapsPlaces(): void {
  if (document.getElementById('google-maps-places-script')) return
  if ((window as any).google?.maps?.places) return
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY || ''
  if (!apiKey) return
  const script = document.createElement('script')
  script.id = 'google-maps-places-script'
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
  script.async = true
  document.head.appendChild(script)
}

/**
 * What the page's address tells the form (KTD8): every query parameter but `t`
 * (hidden fields are filled from them by label), the page the visitor came
 * from (`ref`, which FTP's forward appends, else the browser referrer) and the
 * page the form was on (`src`, the original FTP address, else this page).
 */
function pageAddress(searchParams: URLSearchParams) {
  const params: Record<string, string> = {}
  for (const [key, value] of searchParams) {
    if (key !== 't') params[key] = value
  }
  return {
    params,
    referringUrl: searchParams.get('ref') || document.referrer || '',
    targetUrl: searchParams.get('src') || window.location.href,
  }
}

export default function EFormPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('t') ?? ''
  // Read once: the address the visitor landed on.
  const addressRef = useRef<ReturnType<typeof pageAddress> | null>(null)
  if (addressRef.current === null) addressRef.current = pageAddress(searchParams)
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [submitting, setSubmitting] = useState(false)
  // Every write of this visit (uploads, answer saves, submit) runs through it — see eformQueue.
  const visitQueueRef = useRef<EFormVisitQueue | null>(null)
  // Saves each answer as it is entered, when the account has partial saves on (eformAutosave).
  const autosaveRef = useRef<EFormAutosave | null>(null)
  // FTP's hidden callbackPhone field (R23): a person never sees it, so anything in it came from a bot.
  const honeypotRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!token) {
      setState({ status: 'error', message: 'No form token provided. Please check your link.' })
      return
    }

    eformService
      .loadForm(token, addressRef.current?.params ?? {})
      .then((data) => {
        const hasPayment = data.questions.some((q) => q.questionType === 21)
        const hasAddress = data.questions.some((q) => q.questionType === 22)
        if (hasPayment) loadForwardSdk()
        if (hasAddress) loadGoogleMapsPlaces()
        if (isLeadForm(data) && !data.context.isDemo && data.context.patientId !== 'DEMO') loadRecaptcha()
        const queue = createVisitQueue(data.visitToken)
        visitQueueRef.current = queue
        const isPreview = data.context.isDemo === true || data.context.patientId === 'DEMO'
        autosaveRef.current =
          data.partialSaves && !isPreview
            ? createAutosave(
                (answer) => queue.run((visitToken) => eformService.saveAnswer(token, { ...answer, visitToken })),
                data.questions
              )
            : null
        setState({ status: 'ready', data })
      })
      .catch((err: Error & { code?: string; status?: number }) => {
        const code = err.code ?? ''
        let message = err.message ?? 'Something went wrong loading this form.'

        if (code === 'EXPIRED') message = 'This form link has expired.'
        else if (code === 'ALREADY_COMPLETED') message = 'This form has already been submitted.'
        else if (code === 'INACTIVE') message = 'This form is no longer active.'

        setState({ status: 'error', message, code })
      })
  }, [token])

  async function handleSubmit(answers: Record<number, string | string[]>) {
    setSubmitting(true)
    try {
      const questions = state.status === 'ready' ? state.data.questions : []
      const answerArray = toSubmitAnswers(answers, questions)
      // The bot check (U12): lead forms only; a token that cannot be had is '' and never blocks.
      const botCheck = {
        ...(state.status === 'ready' && isLeadForm(state.data) ? { recaptchaToken: await recaptchaToken() } : {}),
        callbackPhone: honeypotRef.current?.value ?? '',
      }

      console.log('[EForm] Submitting form with', answerArray.length, 'answers, token:', token?.substring(0, 20) + '...')
      const queue = visitQueueRef.current ?? (visitQueueRef.current = createVisitQueue(undefined))
      const result = await queue.run((visitToken) =>
        eformService.submitForm(token, {
          answers: answerArray,
          ...(addressRef.current ?? {}),
          ...botCheck,
          ...(visitToken ? { visitToken } : {}),
        })
      )
      // Submit sent every answer, so answers still waiting for a pause in typing need not save.
      // Only now: when Submit fails they still save, and the Incomplete copy keeps them.
      autosaveRef.current?.cancelPending()
      console.log('[EForm Submit Response]', JSON.stringify(result, null, 2))
      if (result.submissionUrl) {
        window.location.href = result.submissionUrl
        return
      }
      setState({
        status: 'success',
        message: result.submissionMessage || 'Thank you! Your form has been submitted successfully.',
        // A form set to produce a PDF copy offers it here, as FTP's page did.
        ...(result.pdfDownload ? { pdfUrl: eformService.pdfDownloadUrl(result.pdfDownload) } : {}),
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err: any) {
      console.error('[EForm Submit Error]', err?.message, err?.code, err?.status, err)
      const code = err?.code ?? ''
      if (code === 'ALREADY_COMPLETED') {
        setState({ status: 'error', message: 'This form has already been submitted.', code })
        return
      }
      // The renderer clears "Submitting..." and shows this, so the visitor can retry.
      // Only the server's messages written for people reach them; anything else
      // (a validation code, a gateway timeout) gets the plain retry line.
      const forPeople = code === 'INVALID_VISIT' || code === 'PREVIEW_ONLY'
      throw new Error(forPeople && err?.message ? err.message : 'Your form could not be submitted. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
          <p className="text-sm text-slate-500 mt-3">Loading your form...</p>
        </div>
      </div>
    )
  }

  if (state.status === 'error') {
    const isExpiry = state.code === 'EXPIRED' || state.code === 'ALREADY_COMPLETED'
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          {isExpiry ? (
            <Clock className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          ) : (
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          )}
          <h2 className="text-lg font-semibold text-slate-800 mb-2">
            {isExpiry ? 'Form Unavailable' : 'Something Went Wrong'}
          </h2>
          <p className="text-sm text-slate-500">{state.message}</p>
        </div>
      </div>
    )
  }

  if (state.status === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Form Submitted</h2>
          <div
            className="text-sm text-slate-500 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: state.message }}
          />
          {state.pdfUrl && (
            <a
              href={state.pdfUrl}
              download
              className="mt-4 inline-block text-sm text-primary underline"
            >
              Download Document
            </a>
          )}
        </div>
      </div>
    )
  }

  return (
    <EFormRenderer
      data={state.data}
      onSubmit={handleSubmit}
      submitting={submitting}
      eformToken={token}
      visitQueue={visitQueueRef.current ?? undefined}
      autosave={autosaveRef.current ?? undefined}
      honeypotRef={honeypotRef}
    />
  )
}
